import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Generic dropdown — a trigger plus a floating panel of selectable options.
 * Matches the SCORING.FIT design (white rounded card, soft shadow, zinc border,
 * light option text with an optional highlighted selected row).
 *
 * Props:
 *  - trigger:   ({ open, toggle }) => ReactNode — wire onClick={toggle} on your button
 *  - options:   [{ value, label, icon? }]
 *  - value:     selected option value (that row is highlighted / non-clickable)
 *  - onChange:  (value) => void, called when an option is picked
 *  - allowClear: boolean  (when true, clicking the selected row clears it with
 *                onChange(undefined); default false)
 *  - align:     'center' | 'start'  (option content alignment; default 'center')
 *  - ellipsisLabel: boolean  (truncate each option label to one line with an
 *                ellipsis; set the panel width via `className`. default false)
 *  - dividers:  boolean  (thin line between rows; default true)
 *  - maxHeight: a Tailwind max-h-* class (e.g. 'max-h-48') — enables scroll + bottom fade
 *  - gradient:  boolean  (bottom fade overlay when scrollable; default true)
 *  - hideScrollbar: boolean  (keep scrolling but hide the scrollbar; default false)
 *  - placement: 'left' | 'center' | 'right'  (panel horizontal anchor relative to
 *                the trigger wrapper; 'center' centers the panel on it; default 'right')
 *  - className: extra classes for the panel — set the width here, e.g. 'w-20' / 'w-40'
 *  - wrapperClassName: extra classes for the relative wrapper around the trigger
 */
export default function SDropDown({
    trigger,
    options = [],
    value,
    onChange,
    allowClear = false,
    align = 'center',
    ellipsisLabel = false,
    dividers = true,
    maxHeight,
    gradient = true,
    hideScrollbar = false,
    placement = 'center',
    className = '',
    wrapperClassName = '',
}) {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)
    const scrollRef = useRef(null)
    const [atBottom, setAtBottom] = useState(false)
    const toggle = () => setOpen((o) => !o)

    // The fades mean "there's more in this direction", so hide the top fade at the
    // top and the bottom fade at the end (and both when the list can't scroll).
    const handleScroll = () => {
        const el = scrollRef.current
        if (!el) return
        setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1)
    }

    // Measure before paint when the panel opens / options change, so the fade
    // starts in the right state without a flash.
    useLayoutEffect(() => {
        if (open) handleScroll()
    }, [open, options])

    // Close on outside click + Esc
    useEffect(() => {
        if (!open) return

        const handleClick = (e) => {
            if (ref.current?.contains(e.target)) return
            setOpen(false)
        }
        const handleKey = (e) => {
            if (e.key === 'Escape') setOpen(false)
        }

        document.addEventListener('mousedown', handleClick)
        document.addEventListener('keydown', handleKey)
        return () => {
            document.removeEventListener('mousedown', handleClick)
            document.removeEventListener('keydown', handleKey)
        }
    }, [open])

    const padding = align === 'center' ? 'p-2.5' : 'py-2.5 pl-4 pr-2'
    const justify = align === 'center' ? 'justify-center' : 'justify-start'

    // Horizontal anchor of the floating panel within the relative wrapper.
    // 'center' pins the panel's midpoint to the wrapper's midpoint.
    const placementClass =
        placement === 'left' ? 'left-0' : placement === 'center' ? 'left-1/2 -translate-x-1/2' : 'right-0'

    return (
        <div ref={ref} className={`relative ${wrapperClassName}`}>
            {typeof trigger === 'function' ? trigger({ open, toggle }) : trigger}

            {open && (
                <div
                    className={`absolute top-[calc(100%+6px)] z-50 ${placementClass} overflow-hidden rounded-[10px] border border-zinc-300 bg-white shadow-[0px_2px_10px_-2px_rgba(0,0,0,0.10)] ${className}`}
                >
                    <div
                        ref={scrollRef}
                        onScroll={handleScroll}
                        className={`flex flex-col overscroll-contain ${
                            maxHeight ? `${maxHeight} overflow-y-auto` : ''
                        } ${hideScrollbar ? 'scrollbar-none' : ''}`}
                    >
                        {options.map((opt, index) => {
                            const active = opt.value === value
                            return (
                                <div
                                    key={opt.value}
                                    onClick={() => {
                                        if (active) {
                                            if (!allowClear) return
                                            onChange?.(undefined)
                                            setOpen(false)
                                            return
                                        }
                                        onChange?.(opt.value)
                                        setOpen(false)
                                    }}
                                    className={`inline-flex h-8 w-full items-center gap-2.5 ${padding} ${justify} ${
                                        dividers && index !== 0 ? 'border-t border-zinc-300' : ''
                                    } ${active ? 'bg-[#f9f9f9]' : ''} ${
                                        active && !allowClear
                                            ? 'cursor-not-allowed'
                                            : 'cursor-pointer hover:bg-neutral-100'
                                    }`}
                                >
                                    {opt.icon}
                                    <div
                                        title={ellipsisLabel && typeof opt.label === 'string' ? opt.label : undefined}
                                        className={`text-sm ${
                                            active ? 'font-bold text-[#272727]' : 'font-light text-[#272727]'
                                        } ${ellipsisLabel ? 'min-w-0 truncate' : ''}`}
                                    >
                                        {opt.label}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    {maxHeight && gradient && (
                        <>
                            <div
                                className={`pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-linear-to-b from-white/0 to-white transition-opacity duration-200 ${
                                    atBottom ? 'opacity-0' : 'opacity-100'
                                }`}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    )
}
